import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Clock, Check, X, MessageSquare, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Header from '../components/layout/Header';
import BottomNav from '../components/layout/BottomNav';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const OffersPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, offers, products, respondToOffer, getUserByIdSync } = useApp();
  const [activeTab, setActiveTab] = useState('received');

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Offers" showBack />
        <div className="flex flex-col items-center justify-center h-[60vh] px-6">
          <Tag className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view offers</h2>
          <p className="text-gray-500 text-center mb-4">Track offers you've made and received</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Filter offers - received (as seller) and sent (as buyer)
  const receivedOffers = offers.filter(o => o.sellerId === user?.id);
  const sentOffers = offers.filter(o => o.buyerId === user?.id);

  const getProduct = (productId) => products.find(p => p.id === productId);
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case 'accepted':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> Accepted</span>;
      case 'declined':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><X className="w-3 h-3" /> Declined</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const handleAccept = (offerId) => {
    respondToOffer(offerId, 'accepted');
  };

  const handleDecline = (offerId) => {
    respondToOffer(offerId, 'declined');
  };

  const OfferCard = ({ offer, type }) => {
    const product = getProduct(offer.productId);
    const otherUser = type === 'received' 
      ? getUserByIdSync(offer.buyerId) 
      : getUserByIdSync(offer.sellerId);

    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex gap-3">
          {/* Product Image */}
          <div 
            className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
            onClick={() => product && navigate(`/product/${product.id}`)}
          >
            <img 
              src={product?.images?.[0] || 'https://via.placeholder.com/80'} 
              alt={product?.title || 'Product'} 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Offer Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-gray-900 truncate">{product?.title || 'Product'}</h3>
              {getStatusBadge(offer.status)}
            </div>
            
            <div className="mt-1 flex items-center gap-2">
              <span className="text-lg font-bold text-minted-600">${offer.offerPrice}</span>
              <span className="text-sm text-gray-400 line-through">${offer.originalPrice}</span>
              <span className="text-xs text-minted-600 bg-minted-50 px-1.5 py-0.5 rounded">
                {Math.round((1 - offer.offerPrice / offer.originalPrice) * 100)}% off
              </span>
            </div>

            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
              <img 
                src={otherUser?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherUser?.name || 'User')}&background=00c978&color=fff`}
                alt={otherUser?.name}
                className="w-5 h-5 rounded-full"
              />
              <span>{type === 'received' ? 'From' : 'To'}: {otherUser?.name || 'User'}</span>
              <span className="text-gray-300">•</span>
              <span>{formatDate(offer.createdAt)}</span>
            </div>

            {offer.message && (
              <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2 italic">
                "{offer.message}"
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {type === 'received' && offer.status === 'pending' && (
          <div className="mt-4 flex gap-2">
            <Button 
              onClick={() => handleAccept(offer.id)}
              className="flex-1 bg-minted-600 hover:bg-minted-700"
              size="sm"
            >
              <Check className="w-4 h-4 mr-1" /> Accept
            </Button>
            <Button 
              onClick={() => handleDecline(offer.id)}
              variant="outline"
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
              size="sm"
            >
              <X className="w-4 h-4 mr-1" /> Decline
            </Button>
            <Button 
              onClick={() => navigate('/messages')}
              variant="outline"
              size="sm"
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
          </div>
        )}

        {type === 'sent' && offer.status === 'accepted' && (
          <div className="mt-4">
            <Button 
              onClick={() => product && navigate(`/checkout/${product.id}`)}
              className="w-full bg-minted-600 hover:bg-minted-700"
              size="sm"
            >
              Proceed to Checkout <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  const EmptyState = ({ type }) => (
    <div className="flex flex-col items-center justify-center py-12 px-6">
      <Tag className="w-12 h-12 text-gray-300 mb-3" />
      <h3 className="font-medium text-gray-900 mb-1">
        {type === 'received' ? 'No offers received yet' : 'No offers made yet'}
      </h3>
      <p className="text-sm text-gray-500 text-center">
        {type === 'received' 
          ? 'When buyers make offers on your listings, they\'ll appear here' 
          : 'Make an offer on items you\'re interested in'}
      </p>
      {type === 'sent' && (
        <Button 
          onClick={() => navigate('/')}
          className="mt-4 bg-minted-600 hover:bg-minted-700"
          size="sm"
        >
          Browse Items
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header title="Offers" showBack />
      
      <div className="max-w-lg mx-auto px-4 pt-16 pb-4">
        <Tabs defaultValue="received" onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4 bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="received" className="relative data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md py-2">
              Received
              {receivedOffers.filter(o => o.status === 'pending').length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {receivedOffers.filter(o => o.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md py-2">
              Sent
            </TabsTrigger>
          </TabsList>

          <TabsContent value="received" className="space-y-3">
            {receivedOffers.length > 0 ? (
              receivedOffers.map(offer => (
                <OfferCard key={offer.id} offer={offer} type="received" />
              ))
            ) : (
              <EmptyState type="received" />
            )}
          </TabsContent>

          <TabsContent value="sent" className="space-y-3">
            {sentOffers.length > 0 ? (
              sentOffers.map(offer => (
                <OfferCard key={offer.id} offer={offer} type="sent" />
              ))
            ) : (
              <EmptyState type="sent" />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <BottomNav />
    </div>
  );
};

export default OffersPage;
